import {
  BellIcon,
  BuildingOfficeIcon,
  CheckIcon,
  GlobeAltIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { CustomFlowbiteTheme, TabsRef } from 'flowbite-react';
import { Tabs } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { formatDate, formatDateStr } from '../../../../common-utils/formatters';
import { fetchBodaccHistory, resetBodaccHistory } from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import classNames from '../../../utils/classNames';
import { BodaccDict } from '../../../utils/dictionnayMap';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification/ToastrNotif.module';
import Popup from '../../common/Popup';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';

interface TrackedSiret {
  id: number;
  siret: string;
  companyName: string;
  address: string;
  activity: string;
  employeeCount: string;
}

interface BodaccEtablissementLinked {
  siret: string;
  numeroVoieEtablissement?: string;
  typeVoieEtablissement?: string;
  libelleVoieEtablissement?: string;
  codePostalEtablissement?: string;
  libelleCommuneEtablissement?: string;
  denominationUniteLegale?: string;
}

interface TrackedBodaccEntry {
  siren: string;
  familleavis?: string;
  familleavisLib?: string;
  commercant: string;
  dateparution: string;
  etablissementLinked: BodaccEtablissementLinked;
}

interface BodaccPersonne {
  denomination?: string;
  formeJuridique?: string;
}

interface BodaccHistoryItem {
  siren: string;
  familleavis?: string;
  publicationavis?: string;
  numeroannonce?: string;
  dateparution?: string;
  ville?: string;
  numerodepartement?: string;
  tribunal?: string;
  numeroVoieEtablissement?: string;
  typeVoieEtablissement?: string;
  libelleVoieEtablissement?: string;
  codePostalEtablissement?: string;
  libelleCommuneEtablissement?: string;
  listepersonnes?: Record<string, BodaccPersonne>;
  jugement?: Record<string, unknown>;
  acte?: Record<string, unknown>;
  modificationsgenerales?: Record<string, unknown>;
}

const headers = [
  { title: 'SIREN' },
  { title: 'Nom SIREN' },
  { title: 'Nom commercial' },
  { title: 'Type d\'alerte' },
  { title: 'Adresse' },
  { title: 'Date' },
  { title: 'Voir le détail', class: 'min-w-[120px]' },
];

const sortableColumns = [
  'SIREN', 'Nom SIREN', 'Nom commercial', 'Type d\'alerte', 'Adresse', 'Date',
];

const labelTypeAvis = {
  radiation: 'Radiation',
  vente: 'Vente',
  collective: 'Procédure collective',
  modification: 'Modification',
  creation: 'Création',
  immatriculation: 'Immatriculation',
};

const validateSiret = (siret: string): boolean => {
  // Check if the SIRET is exactly 14 digits
  if (!/^\d{14}$/.test(siret)) {
    return false;
  }

  // Luhn algorithm check
  let sum = 0;
  let isEven = false;

  for (let i = siret.length - 1; i >= 0; i--) {
    let digit = parseInt(siret.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

const customTheme: CustomFlowbiteTheme['tabs'] = {
  base: 'flex flex-col',
  tablist: {
    base: 'flex text-center',
    tabitem: {
      base: 'flex items-center justify-center rounded-t-lg p-4 text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 w-full md:w-auto',
      variant: {
        default: {
          base: 'text-gray-500 dark:text-gray-400',
          active: {
            on: 'active bg-gray-100 p-4 text-rgpd-blue',
            off: 'bg-white hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white',
          },
        },
      },
    },
  },
};

function TrackedCompanies() {
  const dispatch = useAppDispatch();

  const company = useAppSelector((state) => state.auth.company);
  const bodaccHistory = useAppSelector((state) => state.copro.bodaccHistory);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [trackedSirets, setTrackedSirets] = useState<TrackedSiret[]>([]);
  const [newSiret, setNewSiret] = useState('');
  const jwt = useAppSelector((state) => state.auth.token);
  
  const [trackedBodaccs, setTrackedBodaccs] = useState(null);
  const [isLoadingTrackedSirets, setIsLoadingTrackedSirets] = useState(true);

  // New state for email notification
  const [notificationEmail, setNotificationEmail] = useState('');

  const tabsRef = useRef<TabsRef>(null);

  const fetchTrackedSirets = async() => {
    setIsLoadingTrackedSirets(true);
    try {
      const response = await fetch(`/api/companies/${company.id}/sirets-tracked`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();

      // If there are no tracked SIRETs, switch to the second tab to set them up
      if (data.length === 0) {
        tabsRef.current?.setActiveTab(1);
      }
      
      // Fetch additional data for each SIRET
      const siretDataPromises = data.map(async(siret) => {
        const siretResponse = await fetch(`/api/sirens/${siret.siret}`, JWTHeader(jwt));
        if (siretResponse.ok) {
          const siretData = await siretResponse.json();
          return {
            id: siret.id,
            siret: siret.siret,
            companyName: siretData.denominationUniteLegale || 'N/A',
            address: `${siretData.numeroVoieEtablissement || ''} ${siretData.typeVoieEtablissement || ''} ${siretData.libelleVoieEtablissement || ''}, ${siretData.codePostalEtablissement || ''} ${siretData.libelleCommuneEtablissement || ''}`.trim(),
            activity: `${siretData.activitePrincipaleUniteLegale || 'N/A'} (${siretData.nomenclatureActivitePrincipaleUniteLegale || 'N/A'})`,
            employeeCount: siretData.trancheEffectifsUniteLegale || 'N/A',
          };
        }
        return siret;
      });

      const enrichedSirets = await Promise.all(siretDataPromises);
      setTrackedSirets(enrichedSirets);
    } catch (error) {
      console.error('Error fetching tracked SIRETs :', error);
      showToast('error', 'Échec du chargement des SIRET suivis.');
    } finally {
      setIsLoadingTrackedSirets(false);
    }
  };

  // Add a new function to fetch tracked BODACCs
  const fetchTrackedBodaccs = async() => {
    try {
      const response = await fetch(`/api/companies/${company.id}/sirets-tracked/bodaccs`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setTrackedBodaccs(data);
    } catch (error) {
      console.error('Error fetching tracked BODACCs :', error);
      showToast('error', 'Échec du chargement des BODACC suivis.');
    }
  };


  useEffect(() => {
    if (company && company.id && jwt) {
      fetchTrackedSirets();
      fetchTrackedBodaccs();
      setNotificationEmail(company.anomalyNotifier || '');
    }
  }, [company, jwt]);

  const handleAddSiret = async() => {
    // Remove any spaces from the SIRET
    const cleanedSiret = newSiret.replaceAll(/\s/g, '');

    // Check if the SIRET is already tracked
    if (trackedSirets.some(siret => siret.siret === cleanedSiret)) {
      showToast('warning', 'Vous suivez déjà cette entreprise.');
      return;
    }

    // Validate SIRET format and checksum
    if (!validateSiret(cleanedSiret)) {
      showToast('error', 'Le format du SIRET est invalide ou le SIRET est incorrect.');
      return;
    }

    try {
      const response = await fetch(`/api/companies/${company.id}/sirets-tracked`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...JWTHeader(jwt).headers,
        },
        body: JSON.stringify({ siret: cleanedSiret }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      setNewSiret('');
      await fetchTrackedSirets();
      await fetchTrackedBodaccs();
      showToast('success', 'SIRET ajouté avec succès.');
    } catch (error) {
      console.error('Error adding SIRET :', error);
      showToast('error', 'Échec de l\'ajout du SIRET.');
    }
  };

  const handleRemoveSiret = async(id: number) => {
    try {
      const response = await fetch(`/api/companies-sirets-tracked/${id}`, {
        method: 'DELETE',
        ...JWTHeader(jwt),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      await fetchTrackedSirets();
      await fetchTrackedBodaccs();
      showToast('success', 'SIRET supprimé avec succès.');
    } catch (error) {
      console.error('Error removing SIRET :', error);
      showToast('error', 'Échec de la suppression du SIRET.');
    }
  };

  const handleDetails = (SIRET: string) => {
    setIsPopupOpen(true);
    dispatch(fetchBodaccHistory(SIRET));
  };

  const renderNestedObject = (obj: Record<string, unknown> | object, count = 0) => Object.keys(obj as Record<string, unknown>).map((key, index) => {
    count++;
    const value = obj[key];
    const displayLabel = (BodaccDict[key] ? BodaccDict[key] : key);
        if (typeof value === 'object' && value !== null) {
      return (
        <div key={`bodacc${displayLabel}${count}${index}`} className="ml-4">
          {displayLabel}
          {' '}
          :
          {' '}
          {renderNestedObject(value as Record<string, unknown>, count + 1)}
        </div>
      );
    }
    return (
      <div key={`bodacc${displayLabel}${count}${index}`} className="ml-4">
        {displayLabel}
        {' '}
        :
        {' '}
        {value}
      </div>
    );
  });

  const coloringBodaccText = (jsx: JSX.Element | string, type: string) => {
    // return a span wrapping the string with the right color
    switch (type) {
    case 'Radiation':
      return <span className="text-red-600">{jsx}</span>;
    case 'Vente':
      return <span className="text-amber-500">{jsx}</span>;
    case 'Procédure collective':
      return <span className="text-red-600">{jsx}</span>;
    default:
      return jsx;
    }
  };

  const renderBodaccData = (bodaccEntry: TrackedBodaccEntry) => {
    const famBodacc = labelTypeAvis[bodaccEntry.familleavis] || bodaccEntry.familleavisLib;
    const address = [
      bodaccEntry.etablissementLinked.numeroVoieEtablissement,
      bodaccEntry.etablissementLinked.typeVoieEtablissement,
      bodaccEntry.etablissementLinked.libelleVoieEtablissement,
      bodaccEntry.etablissementLinked.codePostalEtablissement,
      bodaccEntry.etablissementLinked.libelleCommuneEtablissement,
    ].filter(Boolean).join(' ');
    return {
      SIREN: coloringBodaccText(bodaccEntry.siren, famBodacc),
      'Nom SIREN': coloringBodaccText(bodaccEntry.etablissementLinked.denominationUniteLegale, famBodacc),
      'Nom commercial': coloringBodaccText(bodaccEntry.commercant, famBodacc),
      'Type d\'alerte': coloringBodaccText(famBodacc, famBodacc),
      Adresse: <div className="flex items-center">
        <div>
          <RedirectionMapButtons address={address} />
        </div>
        <span>
          {coloringBodaccText(
            address,
            famBodacc,
          )}
        </span>
      </div>,
      Date: coloringBodaccText(formatDate(bodaccEntry.dateparution), famBodacc),
      'Voir le détail': <div className="flex justify-center">
        <button className="align-center flex w-full items-center justify-center rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto" onClick={() => handleDetails(bodaccEntry.etablissementLinked.siret)}>
          Voir le détail
        </button>
      </div>,
    };
  };

  const trackedSiretsHeaders = [
    { title: 'SIRET' },
    { title: 'Nom de l\'entreprise' },
    { title: 'Adresse' },
    { title: 'Activité principale' },
    { title: 'Effectif' },
    { title: 'Actions' },
  ];

  const trackedSiretsSortableColumns = [
    'SIRET', 'Nom de l\'entreprise', 'Adresse', 'Activité principale', 'Effectif',
  ];

  const renderTrackedSiretsData = (siret: TrackedSiret) => ({
    SIRET: siret.siret,
    'Nom de l\'entreprise': siret.companyName,
    Adresse: siret.address,
    'Activité principale': siret.activity,
    Effectif: siret.employeeCount,
    Actions: (
      <button
        onClick={() => handleRemoveSiret(siret.id)}
        className="flex items-center text-red-600"
      >
        <TrashIcon className="mr-2 h-5 w-5" />
        Supprimer
      </button>
    ),
  });

  const handleSaveNotificationEmail = async() => {
    try {
      const response = await fetch(`/api/companies/${company.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({ anomalyNotifier: notificationEmail }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      showToast('success', 'Adresse e-mail de notification enregistrée avec succès.');
    } catch (error) {
      console.error('Error saving notification email :', error);
      showToast('error', 'Échec de l\'enregistrement de l\'adresse e-mail de notification.');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <p>
            L'outil "
            <span className="font-bold text-rgpd-blue-light">Surveillance gestion locative</span>
            " est essentiel pour les gestionnaires immobiliers.<br/> 
            Cette section vous permet de
            {' '}
            <span className="font-bold text-rgpd-blue-light">suivre spécifiquement les entreprises de votre choix</span>
            {' '}
            en surveillant leurs publications au BODACC.
            <br />
            {' '}
            Ajoutez les SIRET des entreprises que vous souhaitez surveiller, et vous serez informé de tous les changements importants les concernant, tels que les procédures collectives, les ventes, ou les radiations. Cette surveillance ciblée vous permet d'anticiper les risques et de réagir rapidement aux évolutions de la situation des entreprises liées à vos biens immobiliers.
          </p>
        </div>
        
        <Tabs
          aria-label="Tracked companies tabs"
          className="rounded"
          theme={customTheme}
          ref={tabsRef}
        >
          <Tabs.Item title="Historique des alertes BODACC" icon={BuildingOfficeIcon}>
            <h2 className="mb-4 text-xl font-bold">Historique</h2>
            <div className="mb-2 w-full overflow-x-scroll">
              {trackedBodaccs === null ? (
                <>
                  <MUITable
                    data={[]}
                    headers={headers}
                    sortableColumns={[]}
                    isSearchable={false}
                  />
                  <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
                </>
              ) : trackedBodaccs.length === 0 ? (
                <div className="flex w-full place-content-center">
                  <Badge
                    mode="warning"
                    text={trackedSirets.length === 0 
                      ? "Aucune entreprise suivie. Veuillez ajouter des SIRET dans l'onglet 'Paramètres de surveillance' pour commencer le suivi."
                      : 'Aucune entreprise en défaillance dans les 6 derniers mois pour les SIRETs suivis'}
                    className="my-10"
                  />
                </div>
              ) : (
                <MUITable
                  data={trackedBodaccs.map(renderBodaccData)}
                  headers={headers}
                  sortableColumns={sortableColumns}
                  isSearchable={true}
                  filterColumnName={'Type d\'alerte'}
                />
              )}
            </div>
            {isPopupOpen && (
              <Popup
                isOpen
                title="Détails BODACC"
                onClose={() => {
                  setIsPopupOpen(false);
                  dispatch(resetBodaccHistory());
                }}
              >
                {bodaccHistory && bodaccHistory.length > 0 && (
                  <div className="max-h-[75dvh] gap-2 overflow-y-scroll">
                    <a
                      key="linkBODACC-site"
                      className="block w-full cursor-pointer rounded-xl bg-rgpd-blue-light p-2 text-center text-sm text-rgpd-white md:inline-block md:w-auto"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://www.bodacc.fr/pages/annonces-commerciales/?q.registre=registre:${bodaccHistory[0].siren}`}
                    >
                      <GlobeAltIcon className="inline h-4 w-4" />
                      {' '}
                      VOIR SUR LE SITE
                    </a>
                    {(bodaccHistory as unknown as BodaccHistoryItem[]).map((item, idx) => (
                      <div
                        key={`popup-parentbodacc-${idx}`}
                        className={
                          classNames(
                            'p-4 border rounded-lg border-l-4 mt-3',
                            item.familleavis && item.familleavis == 'vente' && 'border-amber-300',
                            item.familleavis && item.familleavis == 'radiation' && 'border-red-500',
                            item.familleavis && item.familleavis == 'collective' && 'border-red-500',
                          )
                        }
                      >
                        <h3 className="font-bold">
                          Jugement{' '}
                          {labelTypeAvis[item.familleavis]}
                        </h3>
                        <div key={`popup-details-bodacc-${idx}`}>
                          Annonce
                          {' '}
                          {item.publicationavis}
                          /N°
                          {item.numeroannonce}
                          {' '}
                          publiée le
                          {' '}
                          {formatDateStr(item.dateparution)}
                          <br />
                          RCS :
                          {' '}
                          {item.siren}
                          {' '}
                          -
                          {' '}
                          {item.ville}
                          {' '}
                          - Département :
                          {' '}
                          {item.numerodepartement}
                          <br />
                          Annonce déposée au
                          {' '}
                          {item.tribunal}
                          <br />
                          {item.listepersonnes && (
                            <>
                              Personnes :
                              {' '}
                              <br />
                              {item.listepersonnes && Object.values(item.listepersonnes as Record<string, BodaccPersonne>).map((personne: BodaccPersonne, personIndex: number) => (
                                <div key={`person-${personne.denomination}-${personIndex}`} className="ml-4">
                                  Dénomination :
                                  {' '}
                                  {personne.denomination}
                                  <br />
                                  Forme juridique :
                                  {' '}
                                  {personne.formeJuridique}
                                  <br />
                                </div>
                              ))}
                            </>
                          )}
                          Adresse de la copro concernée :
                          {' '}
                          {item.numeroVoieEtablissement}
                          {' '}
                          {item.typeVoieEtablissement}
                          {' '}
                          {item.libelleVoieEtablissement}
                          {' '}
                          {item.codePostalEtablissement}
                          {' '}
                          {item.libelleCommuneEtablissement}
                          <br />
                          {item.jugement && (
                            <div className="mt-2 border border-dashed border-slate-600 p-2">
                              <b>Jugement prononçant : </b>
                              <br />
                              {renderNestedObject(item.jugement)}
                            </div>
                          )}
                          {item.acte && (
                            <div className="mt-2 border border-dashed border-slate-600 p-2">
                              <b>Acte effectué : </b>
                              <br />
                              {renderNestedObject(item.acte)}
                            </div>
                          )}
                          {item.modificationsgenerales && (
                            <div className="mt-2 border border-dashed border-slate-600 p-2">
                              <b>Modifications générales :</b>
                              {' '}
                              <br />
                              {renderNestedObject(item.modificationsgenerales)}
                            </div>
                          )}
                        </div>
                        {}
                      </div>
                    ))}
                  </div>
                )}
                {!bodaccHistory && (
                  <Skeleton count={10} height={25} width={400} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
                )}
              </Popup>
            )}
          </Tabs.Item>

          <Tabs.Item title="Paramètres de surveillance" icon={BellIcon}>
            {/* Email notification section */}
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">Notification par e-mail</h2>
              <p className="mb-4 text-sm">
                Indiquez l'adresse e-mail à laquelle vous souhaitez recevoir les notifications en cas de défaillance (radiation, vente, procédure collective) d'une des entreprises suivies.
              </p>
              <div className="mb-4 flex items-center">
                <input
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="Entrez l'adresse e-mail pour les notifications"
                  className="grow rounded-l border px-2 py-2"
                />
                <button
                  onClick={handleSaveNotificationEmail}
                  className="flex items-center rounded-r border border-rgpd-green bg-rgpd-green px-2 py-2 text-white"
                >
                  <CheckIcon className="mr-0 h-6 w-4 md:mr-2 md:w-6" />
                  <span className="hidden md:block">Enregistrer</span>
                </button>
              </div>
            </div>

            {/* Tracked companies section */}
            <div className="mt-10 mb-8">
              <h2 className="mb-4 text-xl font-bold">Liste des entreprises suivies</h2>
              <div className="mb-4 flex items-center">
                <input
                  type="text"
                  value={newSiret}
                  onChange={(e) => setNewSiret(e.target.value)}
                  placeholder="Entrez un SIRET"
                  className="grow rounded-l border px-2 py-2"
                />
                <button
                  onClick={handleAddSiret}
                  className="flex items-center rounded-r border border-rgpd-blue-dark bg-rgpd-blue-dark px-2 py-2 text-white"
                >
                  <PlusIcon className="mr-0 h-6 w-4 md:mr-2 md:w-6" />
                  <span className="hidden md:block">Ajouter</span>
                </button>
              </div>
              {isLoadingTrackedSirets ? (
                <>
                  <MUITable
                    data={[]}
                    headers={trackedSiretsHeaders}
                    sortableColumns={[]}
                    isSearchable={false}
                  />
                  <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
                </>
              ) : trackedSirets.length > 0 ? (
                <MUITable
                  data={trackedSirets.map(renderTrackedSiretsData)}
                  headers={trackedSiretsHeaders}
                  sortableColumns={trackedSiretsSortableColumns}
                  isSearchable={false}
                  disablePagination
                />
              ) : (
                <div className="flex w-full place-content-center">
                  <Badge
                    mode="warning"
                    text="Aucun SIRET suivi. Veuillez ajouter des SIRET pour commencer la surveillance."
                    className="my-4"
                  />
                </div>
              )}
            </div>
          </Tabs.Item>
        </Tabs>
      </div>
    </div>
  );
}

export default TrackedCompanies;
